import { type FC, useEffect, useRef, useState } from 'react'

import './_inputCard.scss'
import { useTranslation } from 'react-i18next'
import { Tooltip as ReactTooltip } from 'react-tooltip'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { IconButton } from 'components/IconButton/IconButton'
import ImgInCircle from 'components/UI/ImgInCircle/ImgInCircle'
import { AllIcons } from 'helpers/allIcons'
import { Icons } from 'helpers/icons'
import { useUserProfile } from 'store'
import { type IInputCard } from 'types/interfaces/IInputCard'

const InputCard: FC<IInputCard> = ({
    hasAvatar = false,
    hasIcon = false,
    icon,
    type,
    label,
    value,
    changeFn,
    keyDownFn,
    errorActive,
    errorMessage,
    hasTypeChanger,
    validate,
    customClass,
    isTextArea,
    hasDesc,
    desc,
    info,
    disabled = false,
    maxChars,
    iconRight,
    iconSmaller,
    placeholder,
    validateFn,
    hasError,
    isValid,
    blurFn,
    focusFn,
    mask,
    externalInputRef,
    inputProps,
    hideLabelWhenFilled = false
}) => {
    const [inputType, setInputType] = useState<string>('text')
    const [inputError, setInputError] = useState<boolean>(false)
    const [inputErrorMsg, setInputErrorMsg] = useState<string>('')
    const [inputFocused, setInputFocused] = useState(false)

    const userData = useUserProfile()
    const { t } = useTranslation()

    const inputRef = useRef<any>(null)
    const labelRef = useRef<any>(null)

    useEffect(() => {
        setInputType(type)
    }, [type])

    useEffect(() => {
        if (!labelRef.current) return

        if (`${value}`.length > 0) {
            labelRef.current.classList.add('inputcard__label--filled')
        } else {
            labelRef.current.classList.remove('inputcard__label--filled')
        }
    }, [value])

    const validateInput = (text: string): boolean => {
        switch (validate) {
            case 'required':
                if (text.length > 0) {
                    setInputError(false)
                    return false
                } else {
                    setInputErrorMsg(t('error:errorRequiredField'))
                    setInputError(true)
                    return true
                }

            case 'letters':
                if (/^[a-zA-Z\s]*$/.test(text)) {
                    setInputError(false)
                    return false
                } else {
                    setInputErrorMsg(t('error:errorLettersValidation'))
                    setInputError(true)
                    return true
                }

            case 'letters&required':
                if (/^[a-zA-Z\s]*$/.test(text) && text.length > 0) {
                    setInputError(false)
                    return false
                } else {
                    setInputErrorMsg(t('error:errorLettersRequiredValidation'))
                    setInputError(true)
                    return true
                }

            case 'number':
                if (/[0-9]/.test(text)) {
                    setInputError(false)
                    return false
                } else {
                    setInputErrorMsg(t('error:errorNumbersValidation'))
                    setInputError(true)
                    return true
                }

            case 'numbers&required':
                if (/[0-9]/.test(text) && text.length > 0) {
                    setInputError(false)
                    return false
                } else {
                    setInputErrorMsg(t('error:errorNumbersRequiredValidation'))
                    setInputError(true)
                    return true
                }

            case 'letters/numbers':
                if (`${value}`.length < 8) {
                    inputRef.current.classList.add('inputcard__input--error')
                    setInputError(true)
                    setInputErrorMsg(t('error:errorUserNameMinChar'))
                    return true
                } else {
                    if (/^[a-zA-Z0-9._]*$/.test(text)) {
                        setInputError(false)
                        return false
                    } else {
                        setInputErrorMsg(t('error:errorUserNameInputType'))
                        setInputError(true)
                        return true
                    }
                }

            case 'password':
                if (
                    /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/.test(text) &&
                    text.length > 0
                ) {
                    setInputError(false)
                    return false
                } else {
                    setInputErrorMsg(t('error:errorPasswordVaidation'))
                    setInputError(true)
                    return true
                }

            case 'email':
                if (/\S+@\S+\.\S+/.test(text) && text.length > 0) {
                    setInputError(false)
                    return false
                } else {
                    setInputErrorMsg(t('error:errorEmail'))
                    setInputError(true)
                    return true
                }

            case 'cvc':
                if (/[0-9]/.test(text) && text.length === 3) {
                    setInputError(false)
                    return false
                } else {
                    setInputError(true)
                    return true
                }

            case 'cardNo':
                if (
                    /[0-9]/.test(text) &&
                    text.length >= 15 &&
                    text.length <= 16
                ) {
                    setInputError(false)
                    return false
                } else {
                    setInputError(true)
                    return true
                }
        }
        return false
    }

    return (
        <div
            className={`inputcard ${
                hasAvatar || hasIcon ? 'inputcard--withavatar' : ''
            }
      ${iconRight ? 'inputcard--withavatar--right' : ''}
      ${customClass ?? ''}`}
        >
            {hasAvatar ? (
                <ImgInCircle
                    type='profile--small'
                    customClass='inputcard__avatar'
                >
                    <img
                        src={userData?.avatar?.url ?? placeholderAvatar}
                        alt={t('profileAvatar')}
                    />
                </ImgInCircle>
            ) : (
                ''
            )}
            {hasIcon ? (
                <IconButton
                    icon={icon}
                    customClass={`inputcard__icon ${
                        iconRight ? 'inputcard__icon--right' : ''
                    } ${iconSmaller ? 'inputcard__icon--smaller' : ''}`}
                />
            ) : (
                ''
            )}
            {!isTextArea ? (
                <>
<input
  disabled={disabled}
  ref={(el) => {
    inputRef.current = el

    if (typeof externalInputRef === 'function') {
      externalInputRef(el)
    } else if (externalInputRef) {
      ;(externalInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
    }
  }}
  {...inputProps}
  type={inputType}
  placeholder={placeholder ?? ''}
className={`inputcard__input ${
    disabled ? 'disabled' : ''
} ${
    value && !hideLabelWhenFilled
        ? 'inputcard__input--filled'
        : ''
} ${
    inputType === 'password'
        ? 'inputcard__input--pass'
        : ''
} ${
    inputError
        ? 'inputcard__input--error'
        : ''
}
${!inputFocused && value && mask ? 'inputcard-input-masked' : ''}`}
  value={value}
  maxLength={maxChars && maxChars}
  onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
    inputProps?.onFocus?.(e)
    labelRef.current.classList.add('inputcard__label--hide')
    setInputFocused(true)
    focusFn && focusFn()
  }}
onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
    inputProps?.onBlur?.(e)

    if (
        !hideLabelWhenFilled ||
        !e.currentTarget.value
    ) {
        labelRef.current?.classList.remove(
            'inputcard__label--hide'
        )
    }

    if (inputType === 'string' || validate === 'cardNo') {
        const isFailed = validateInput(`${value}`)
        hasError?.(isFailed)
    }

    setInputFocused(false)
    !isValid && validateFn?.(e.target.value)
    blurFn && blurFn()
}}
  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
    inputProps?.onKeyDown?.(e)
    keyDownFn?.(e)
  }}
  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
    inputProps?.onChange?.(e)
    setInputError(false)

    if (e.currentTarget.value) {
      labelRef.current.classList.add('inputcard__label--hide')
    }

    validateFn?.(e.target.value)
    changeFn(e.currentTarget.value)
  }}
/>
                    {mask && (
                        <div
                            className={`inputcard-mask ${
                                !inputFocused && value
                                    ? 'inputcard-mask-visible'
                                    : ''
                            }`}
                        >
                            {mask}
                        </div>
                    )}
                </>
            ) : (
                <textarea
                    disabled={disabled}
                    ref={inputRef}
                    maxLength={maxChars && maxChars}
                    placeholder={placeholder ?? ''}
                    className={`inputcard__input inputcard__input--textarea ${
                        disabled ? 'disabled' : ''
                    } ${value ? 'inputcard__input--filled' : ''} ${
                        inputType === 'password' ? 'inputcard__input--pass' : ''
                    } ${inputError ? 'inputcard__input--error' : ''}`}
                    value={value}
                    onFocus={() => {
                        labelRef.current.classList.add('inputcard__label--hide')
                        focusFn && focusFn()
                    }}
                    onBlur={() => {
                        labelRef.current.classList.remove(
                            'inputcard__label--hide'
                        )
                        // blurValidation();
                        inputType === 'string' && validateInput(`${value}`)
                        hasError?.(inputError)
                        blurFn && blurFn()
                    }}
                    onChange={(e: any) => {
                        setInputError(false)
                        if (e.currentTarget.value) {
                            labelRef.current.classList.add(
                                'inputcard__label--hide'
                            )
                        }
                        changeFn(e.currentTarget.value)
                    }}
                />
            )}
<span
    ref={labelRef}
    className={`inputcard__label ${
        hideLabelWhenFilled && `${value}`.length > 0
            ? 'inputcard__label--hide'
            : ''
    }`}
>
    {label}
</span>
            {hasTypeChanger ? (
                <img
                    className='inputcard__eye'
                    src={Icons.eye}
                    onClick={() => {
                        inputType === 'password'
                            ? setInputType('text')
                            : setInputType('password')
                        inputRef.current.focus()
                    }}
                    alt={t('togglePasswordVisibility')}
                />
            ) : (
                ''
            )}
            {info ? (
                <div className='inputcard__info'>
                    <img
                        src={AllIcons.info_circle}
                        alt={t('info')}
                        data-tip={info}
                    />
                    <ReactTooltip variant='dark' float={false} />
                </div>
            ) : (
                ''
            )}
            {errorActive ? (
                <div className='inputcard__error'>
                    <p>{errorMessage}</p>
                </div>
            ) : (
                ''
            )}
            {inputError && inputErrorMsg ? (
                <div className='inputcard__error'>
                    <p>{inputErrorMsg}</p>
                </div>
            ) : (
                ''
            )}
            {hasDesc ? <div className='inputcard__desc'>{desc}</div> : ''}
        </div>
    )
}

export default InputCard
